import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';
import { SCHOOL_PORTAL_EVENT } from '@uniqcamp/shared';

interface ToastContextValue {
  showToast: (severity: AlertColor, message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [message, setMessage] = useState('');

  const showToast = useCallback((s: AlertColor, msg: string) => {
    setSeverity(s);
    setMessage(msg);
    setOpen(true);
  }, []);

  useEffect(() => {
    const onForbidden = (e: Event) => {
      const ce = e as CustomEvent<{ message?: string; severity?: AlertColor }>;
      const msg = ce.detail?.message || 'Action not allowed.';
      const sev = ce.detail?.severity === 'error' ? 'error' : 'warning';
      showToast(sev, msg);
    };
    window.addEventListener(SCHOOL_PORTAL_EVENT.API_FORBIDDEN, onForbidden as EventListener);
    return () => window.removeEventListener(SCHOOL_PORTAL_EVENT.API_FORBIDDEN, onForbidden as EventListener);
  }, [showToast]);

  const handleClose = useCallback((_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => handleClose()} severity={severity} variant="filled" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
